import { useCallback, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getProductUUID } from "@/hooks/useCart";
import { invokeCreatePaymentIntent, makeIdempotencyKey } from "@/lib/payments/client";
import { buildOrderConfirmationInvokeArgs } from "@/lib/order-confirmation-email";
import { L, fp, DOP } from "../helpers";
import { PROMO_CODES } from "../data";
import type { Lang, Product, ShipOpt } from "../types";
import type { PayMethod, ShippingId, AzulPending } from "./useCheckout";
import { useExpressEligibility } from "./useExpressEligibility";

type TransferTx = {
  transactionId: string;
  providerRef: string;
  orderId: string;
  userId: string;
  amount: number;
  instructions?: { title: string; steps: string[]; bankAccounts?: any[]; reference: string };
};

interface Args {
  user: any;
  lang: Lang;
  t: any;
  cart: any[];
  cartCnt: number;
  cartTot: number;
  clearCart: () => void;
  toast: (m: string) => void;
  setModal: (m: any) => void;
  // Checkout wizard state
  ckStep: number;
  setCkStep: React.Dispatch<React.SetStateAction<number>>;
  payMethod: PayMethod;
  setPayMethod: (m: PayMethod) => void;
  shipId: ShippingId;
  setShipId: (id: ShippingId) => void;
  shipOpts: ShipOpt[];
  shipCost: number;
  appliedPromo: string | null;
  setAppliedPromo: (c: string | null) => void;
  promoCode: string;
  setPromoCode: (c: string) => void;
  setOrderDone: (v: boolean) => void;
  setAzulPending: (v: AzulPending) => void;
  setCartOpen: (v: boolean) => void;
  // Payment + rewards
  paymentSettings: any;
  rewardPts: number;
  setRewardPts: React.Dispatch<React.SetStateAction<number>>;
  validatedCoach: any;
  clearCoach: () => void;
  // Orders
  setOrders: React.Dispatch<React.SetStateAction<any[]>>;
  // Navigation
  nav: (page: string, prod?: Product | null) => void;
}

/**
 * Encapsulates the full order placement flow:
 * - `promoDisc` memo
 * - `applyPromo`
 * - `placeOrderFlow` (the 200-line monster — DB insert, items, coach
 *   attribution, loyalty redemption, payment intent, email, AZUL handoff)
 * - `doCheckout` / `doExpressCheckout`
 * - local `transferTx` + `pointsToRedeem` state surfaced through the
 *   bank-transfer instructions UI.
 */
export function useCheckoutFlow(args: Args) {
  const {
    user, lang, t, cart, cartCnt, cartTot, clearCart, toast, setModal,
    ckStep, setCkStep, payMethod, setPayMethod, shipId, setShipId, shipOpts, shipCost,
    appliedPromo, setAppliedPromo, promoCode, setPromoCode,
    setOrderDone, setAzulPending, setCartOpen,
    paymentSettings, rewardPts, setRewardPts, validatedCoach, clearCoach,
    setOrders, nav,
  } = args;

  const [pointsToRedeem, setPointsToRedeem] = useState<number>(0);
  const [transferTx, setTransferTx] = useState<TransferTx | null>(null);

  const $ = (usd: number) => fp(usd, lang);

  const promoDisc = useMemo(() => {
    if (!appliedPromo) return 0;
    const p = (PROMO_CODES as any)[appliedPromo];
    if (!p) return 0;
    if (p.type === "percent") return cartTot * p.disc;
    if (p.type === "fixed_dop") return p.disc / DOP;
    return 0;
  }, [appliedPromo, cartTot]);

  const applyPromo = useCallback(() => {
    const code = promoCode.toUpperCase().trim();
    if ((PROMO_CODES as any)[code]) {
      setAppliedPromo(code);
      toast(`🎉 ${t.promoApplied} — ${L((PROMO_CODES as any)[code].desc, lang)}`);
    } else {
      toast(`❌ ${t.invalidPromo}`);
    }
    setPromoCode("");
  }, [promoCode, lang, toast, t, setAppliedPromo, setPromoCode]);

  const placeOrderFlow = useCallback((opts?: {
    skipStepGuard?: boolean;
    overridePayMethod?: PayMethod;
    overrideShipId?: ShippingId;
  }) => {
    const skipStepGuard = !!opts?.skipStepGuard;
    const methodArg = opts?.overridePayMethod ?? payMethod;
    const shipIdArg = opts?.overrideShipId ?? shipId;
    if (!skipStepGuard && ckStep < 2) { setCkStep((s: number) => s + 1); return; }
    const shipOpt = shipOpts.find((o: any) => o.id === shipIdArg) ?? shipOpts[0];
    const shipCostArg = shipOpt ? shipOpt.pr : shipCost;
    const codSurchargeArg = methodArg === "cod"
      ? ((paymentSettings?.cod_surcharge ?? 0) / DOP)
      : 0;
    const preDiscountTotal = cartTot + shipCostArg + codSurchargeArg - promoDisc;
    const coachDiscArg = validatedCoach ? Math.max(0, cartTot * (validatedCoach.discount_pct / 100)) : 0;
    const preDiscountTotalWithCoach = Math.max(0, preDiscountTotal - coachDiscArg);
    const maxRedeemablePts = Math.min(
      rewardPts,
      Math.max(0, Math.floor(preDiscountTotalWithCoach * 100)),
    );
    const redeemPts = Math.max(0, Math.min(pointsToRedeem, maxRedeemablePts));
    const redeemDiscount = redeemPts / 100;
    const finalTotal = preDiscountTotalWithCoach - redeemDiscount;
    setModal({
      title: t.placeOrderQ,
      text: `${cartCnt} ${t.items} — ${$(Math.max(0, finalTotal))}`,
      onConfirm: async () => {
        try {
          // Guest checkout: silently create an anonymous Supabase session so
          // we can still satisfy orders.user_id NOT NULL + RLS without
          // forcing the user through a signup form.
          let effectiveUser = user;
          if (!effectiveUser) {
            const { data: anonData, error: anonErr } = await supabase.auth.signInAnonymously();
            if (anonErr || !anonData?.user) {
              console.error("anonymous sign-in failed:", anonErr);
              toast(`❌ ${lang === "es" ? "No pudimos iniciar tu sesión de invitado. Intenta de nuevo." : "Could not start guest session. Please try again."}`);
              return;
            }
            effectiveUser = anonData.user;
          }
          const total = Math.max(0, finalTotal);
          const { data: orderData, error: orderErr } = await supabase
            .from("orders")
            .insert({ user_id: effectiveUser.id, total_amount: total, subtotal: cartTot, shipping_cost: shipCostArg, tax: 0, payment_method: methodArg, status: "pending" })
            .select()
            .single();
          if (orderErr) throw orderErr;
          const orderItems = cart
            .map((c: any) => {
              const prodUuid = getProductUUID(c.id) || c.dbId || null;
              return {
                order_id: orderData.id,
                product_id: prodUuid,
                variant_id: c.variantId ?? null,
                bundle_id: c.bundleId ?? null,
                quantity: c.qty,
                price_per_item: c.pr,
              };
            })
            .filter((i) => i.product_id);
          if (orderItems.length > 0) {
            const { error: itemsErr } = await supabase.from("order_items").insert(orderItems);
            if (itemsErr) throw itemsErr;
          }
          if (validatedCoach) {
            try {
              const commission = Number(((cartTot * validatedCoach.commission_pct) / 100).toFixed(2));
              await supabase.from("coach_attributions").insert({
                coach_id: validatedCoach.id,
                order_id: orderData.id,
                user_id: effectiveUser.id,
                discount_code: validatedCoach.discount_code,
                order_subtotal: Number(cartTot.toFixed(2)),
                discount_applied: Number(coachDiscArg.toFixed(2)),
                commission_amount: commission,
                status: "pending",
              });
            } catch (e) {
              console.error("coach attribution insert failed:", e);
            }
          }
          if (redeemPts > 0) {
            try {
              const { error: redeemErr } = await supabase.rpc("redeem_loyalty_points", {
                _user_id: effectiveUser.id,
                _points: redeemPts,
                _order_id: orderData.id,
              });
              if (redeemErr) {
                console.error("redeem_loyalty_points error:", redeemErr);
              } else {
                setRewardPts((rp) => Math.max(0, rp - redeemPts));
              }
            } catch (e) {
              console.error("redeem_loyalty_points threw:", e);
            }
          }
          let intent: Awaited<ReturnType<typeof invokeCreatePaymentIntent>> | null = null;
          try {
            intent = await invokeCreatePaymentIntent({
              provider: methodArg as any,
              order_id: orderData.id,
              amount: total,
              currency: "DOP",
              idempotency_key: makeIdempotencyKey(orderData.id, methodArg),
            });
          } catch (e) {
            console.error("payment intent error:", e);
            toast(`⚠️ ${lang === "es" ? "Orden creada pero el pago necesita seguimiento" : "Order created but payment needs follow-up"}`);
          }
          const oid = orderData.id.slice(0, 13).toUpperCase();
          const newOrder = {
            id: oid,
            dbId: orderData.id,
            cust: lang === "es" ? "Tú" : "You",
            items: cart.map((c: any) => ({ pid: c.id, q: c.qty })),
            total,
            status: "Pending" as const,
            date: new Date().toISOString().split("T")[0],
            tracking: null,
            ca: null,
          };
          setOrders((p) => [newOrder, ...p]);
          const pts = Math.round(total * 5);
          setRewardPts((rp) => rp + pts);
          if (methodArg === "azul") {
            setAzulPending({
              orderId: orderData.id,
              amountDop: Math.max(0, total) * DOP,
              itbisDop: 0,
            });
          } else {
            setOrderDone(true);
          }
          clearCart();
          setAppliedPromo(null);
          setPointsToRedeem(0);
          clearCoach();
          setModal(null);
          toast(
            methodArg === "azul"
              ? `💳 ${lang === "es" ? "Completa el pago con tarjeta para confirmar tu pedido" : "Complete the card payment to confirm your order"}`
              : `🎉 ${t.orderPlaced} +${pts} pts`,
          );
          if (intent && methodArg === "manual_transfer") {
            setTransferTx({
              transactionId: intent.transaction_id,
              providerRef: intent.provider_ref ?? "",
              orderId: orderData.id,
              userId: effectiveUser.id,
              amount: total,
              instructions: intent.instructions,
            });
          }
          try {
            const { data: profile } = await supabase.from("profiles").select("email,first_name").eq("user_id", effectiveUser.id).maybeSingle();
            const emailArgs = buildOrderConfirmationInvokeArgs({
              cart, lang, DOP, shipOpts, shipId: shipIdArg, cartTot, shipCost: shipCostArg, total,
              orderId: oid, dbOrderId: orderData.id,
              profile, userEmail: effectiveUser.email,
            });
            if (emailArgs) {
              supabase.functions
                .invoke("send-transactional-email", { body: emailArgs })
                .catch((e: any) => console.error("order email error:", e));
            }
          } catch (e) {
            console.error("order email lookup error:", e);
          }
        } catch (e: any) {
          console.error("Checkout error:", e);
          setModal(null);
          toast(`❌ ${lang === "es" ? "Error al crear orden" : "Error creating order"}`);
        }
      },
    });
  }, [
    ckStep, cart, cartCnt, cartTot, shipCost, shipId, shipOpts, promoDisc, lang, toast, t,
    clearCart, user, payMethod, paymentSettings, setCkStep, setOrders, setRewardPts,
    setOrderDone, setAppliedPromo, setModal, setAzulPending, pointsToRedeem, rewardPts,
    validatedCoach, clearCoach,
  ]);

  const expressEligibility = useExpressEligibility(user?.id || null);

  const doCheckout = useCallback(() => placeOrderFlow(), [placeOrderFlow]);

  const doExpressCheckout = useCallback(async () => {
    if (!expressEligibility.eligible || !expressEligibility.payMethod) return;
    const method = expressEligibility.payMethod;
    setPayMethod(method);
    setShipId("standard");
    setCkStep(2);
    setCartOpen(false);
    nav("checkout");
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const delay = reduceMotion ? 0 : 240;
    await new Promise<void>((resolve) => window.setTimeout(resolve, delay));
    placeOrderFlow({ skipStepGuard: true, overridePayMethod: method, overrideShipId: "standard" });
  }, [expressEligibility, placeOrderFlow, setPayMethod, setShipId, setCkStep, setCartOpen, nav]);

  return {
    promoDisc, applyPromo,
    placeOrderFlow, doCheckout, doExpressCheckout,
    expressEligibility,
    pointsToRedeem, setPointsToRedeem,
    transferTx, setTransferTx,
  };
}